import { useEffect, useRef, useState } from "react";
import { KeypadPicture } from "./SetupPictures";

type Dir = "right" | "up" | "left" | "down";
const KEY_DIR: Record<string, Dir> = {
  ArrowRight: "right", ArrowUp: "up", ArrowLeft: "left", ArrowDown: "down",
  "6": "right", "8": "up", "4": "left", "2": "down",
};
const ARROW: Record<Dir, string> = { right: "→", up: "↑", left: "←", down: "↓" };
const ANGLE: Record<Dir, number> = { right: 0, up: -90, left: 180, down: 90 };
const TRIALS = 5;

/** A large, plain E (not the test's calibrated one): five strokes, prongs to the right. */
function PracticeE({ dir }: { dir: Dir }) {
  return (
    <svg viewBox="0 0 50 50" className="practice-e" role="img" aria-label="a letter E">
      <g transform={`rotate(${ANGLE[dir]} 25 25)`} fill="#111">
        <rect x="0" y="0" width="10" height="50" />
        <rect x="0" y="0" width="50" height="10" /><rect x="0" y="20" width="50" height="10" /><rect x="0" y="40" width="50" height="10" />
      </g>
    </svg>
  );
}

/** The keypad (each arrow once), then a few unscored Es with feedback. */
export function Practice({ busy, onDone, onSkip }: {
  busy?: boolean; onDone: (r: { keys_ok: boolean; trials: number; correct: number }) => void; onSkip?: () => void;
}) {
  const [lit, setLit] = useState<Record<string, boolean>>({});
  const keysOk = ["up", "down", "left", "right"].every((k) => lit[k]);
  const [trial, setTrial] = useState(0);
  const [dir, setDir] = useState<Dir>("right");
  const [correct, setCorrect] = useState(0);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [started, setStarted] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const next = () => setDir((["right", "up", "left", "down"] as Dir[])[Math.floor(Math.random() * 4)]!);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const d = KEY_DIR[e.key];
      const nothing = e.key === " " || e.key === "0";
      if (!d && !nothing) return;
      e.preventDefault();
      if (!started) {
        if (d) setLit((l) => ({ ...l, [d]: true }));
        return;
      }
      if (trial >= TRIALS) return;
      const ok = d === dir;
      setCorrect((c) => c + (ok ? 1 : 0));
      setFeedback(ok ? "Right!" : nothing ? `It pointed ${ARROW[dir]}` : `That E pointed ${ARROW[dir]}`);
      setTrial((t) => t + 1);
      next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [started, trial, dir]);

  return (
    <div ref={box}>
      {!started ? (
        <>
          <p>Press each arrow on the keypad once. Each one turns green.</p>
          <KeypadPicture lit={lit} />
          <p className="small muted">On a number pad: 8 is up, 2 down, 4 left, 6 right. Space (or 0) means “I can’t see it”.</p>
          <div className="row">
            <button className="big" disabled={!keysOk} onClick={() => { setStarted(true); next(); }}>
              {keysOk ? "All four work: practise" : "Press all four arrows"}</button>
            {onSkip && <button className="secondary" onClick={onSkip}>Skip for now</button>}
          </div>
        </>
      ) : trial < TRIALS ? (
        <>
          <p>Which way do the E's bars point? Press that arrow. ({trial + 1} of {TRIALS})</p>
          <div className="practice-stage"><PracticeE dir={dir} /></div>
          <p className="practice-feedback" aria-live="polite">{feedback ?? " "}</p>
        </>
      ) : (
        <>
          <p><strong>{correct} of {TRIALS} right.</strong> In the test the E gets smaller or fainter until it is hard to
            see; then it is fine to guess. Practice answers are not kept as results.</p>
          <button className="big" disabled={busy} onClick={() => onDone({ keys_ok: keysOk, trials: TRIALS, correct })}>Done</button>
        </>
      )}
    </div>
  );
}
